import { CategoryVector, Tag } from "types";
import jsonData from "assets/json/Tags.json";

interface FlattenedCategory {
  path: string[];
  index: number;
}

export class VectorHandling {
  private flattenHierarchy(
    group: string,
    groupData: any
  ): { categories: FlattenedCategory[]; vectorSize: number } {
    const flattened: FlattenedCategory[] = [];

    Object.entries(groupData).forEach(([level1, level1Content]) => {
      flattened.push({ path: [group, level1], index: flattened.length });

      if (Array.isArray(level1Content)) {
        level1Content.forEach((tag) => {
          flattened.push({
            path: [group, level1, tag],
            index: flattened.length,
          });
        });
      } else {
        Object.entries(level1Content as object).forEach(
          ([level2, level2Content]) => {
            flattened.push({
              path: [group, level1, level2],
              index: flattened.length,
            });

            if (Array.isArray(level2Content)) {
              level2Content.forEach((tag) => {
                flattened.push({
                  path: [group, level1, level2, tag],
                  index: flattened.length,
                });
              });
            } else {
              Object.entries(level2Content as object).forEach(
                ([level3, tags]) => {
                  flattened.push({
                    path: [group, level1, level2, level3],
                    index: flattened.length,
                  });
                  (tags as string[]).forEach((tag) => {
                    flattened.push({
                      path: [group, level1, level2, level3, tag],
                      index: flattened.length,
                    });
                  });
                }
              );
            }
          }
        );
      }
    });

    return { categories: flattened, vectorSize: flattened.length };
  }

  private isParentCategory(parentPath: string[], childPath: string[]): boolean {
    if (parentPath.length >= childPath.length) return false;
    return parentPath.every((segment, index) => segment === childPath[index]);
  }

  public createContentVector(tags: string[]): CategoryVector {
    const result: CategoryVector = {
      studyFields: [],
      programLevels: [],
      destination: [],
      requirements: [],
      studyDelivery: [],
      financial: [],
      career: [],
      application: [],
      healthcare: [],
      lifestyle: [],
    };

    Object.entries(jsonData).forEach(([group, groupData]) => {
      const { categories, vectorSize } = this.flattenHierarchy(
        group,
        groupData
      );
      const vector = new Array(vectorSize).fill(0);

      tags.forEach((tag) => {
        const matchingCategories = categories.filter((cat) => {
          const tagPath = cat.path;
          return tagPath[tagPath.length - 1] === tag;
        });

        matchingCategories.forEach((match) => {
          vector[match.index] = 1;

          categories.forEach((cat) => {
            if (this.isParentCategory(cat.path, match.path)) {
              vector[cat.index] = 1;
            }
          });
        });
      });

      result[group as keyof CategoryVector] = vector;
    });

    return result;
  }

  public updateUserPreferenceVector(
    currentVector: CategoryVector | undefined,
    newPostTags: string[],
    decayFactor: number = 0.15
  ): CategoryVector {
    const newPostVector = this.createContentVector(newPostTags);
    if (!currentVector) return newPostVector;

    const result: CategoryVector = {} as CategoryVector;
    const currentWeight = 1 - decayFactor;

    Object.keys(currentVector)
      .filter((category) => category !== "__typename")
      .forEach((category) => {
        const current = currentVector[category as keyof CategoryVector];
        const newVector = newPostVector[category as keyof CategoryVector];

        if (!Array.isArray(current)) {
          console.error(`Invalid vector for category ${category}:`, current);
          result[category as keyof CategoryVector] = newVector;
          return;
        }

        const combinedVector = current.map(
          (value: number, index: number) =>
            value * decayFactor + newVector[index] * currentWeight
        );

        const maxValue = Math.max(...combinedVector);
        const minValue = Math.min(...combinedVector);

        if (maxValue !== minValue) {
          result[category as keyof CategoryVector] = combinedVector.map(
            (value) => (value - minValue) / (maxValue - minValue)
          );
        } else {
          result[category as keyof CategoryVector] = combinedVector;
        }
      });
    console.log(result);
    return result;
  }

  public convertFlatVectorToCategoryVector(
    flatVector: number[]
  ): CategoryVector {
    const result: CategoryVector = {
      studyFields: [],
      programLevels: [],
      destination: [],
      requirements: [],
      studyDelivery: [],
      financial: [],
      career: [],
      application: [],
      healthcare: [],
      lifestyle: [],
    };

    let currentIndex = 0;
    Object.keys(result).forEach((category) => {
      const categoryData = jsonData[category as keyof typeof jsonData];
      const { vectorSize } = this.flattenHierarchy(category, categoryData);

      result[category as keyof CategoryVector] = flatVector.slice(
        currentIndex,
        currentIndex + vectorSize
      );
      currentIndex += vectorSize;
    });

    return result;
  }

  public getTagsFromVector(vector: CategoryVector | number[]): Tag[] {
    const categoryVector = Array.isArray(vector)
      ? this.convertFlatVectorToCategoryVector(vector)
      : vector;

    const result: Tag[] = [];

    Object.entries(categoryVector).forEach(([group, groupVector]) => {
      if (group in jsonData) {
        const { categories } = this.flattenHierarchy(
          group,
          jsonData[group as keyof typeof jsonData]
        );

        groupVector.forEach((value: number, index: number) => {
          if (value > 0) {
            const category = categories[index];
            if (category && category.path.length > 2) {
              result.push({
                tag: category.path[category.path.length - 1],
                group,
                level2Category: category.path[1],
                level3Category:
                  category.path.length > 3 ? category.path[2] : undefined,
              });
            }
          }
        });
      }
    });

    return result;
  }

  public debugVectorTags(vector: CategoryVector | number[]): void {
    const categoryVector = Array.isArray(vector)
      ? this.convertFlatVectorToCategoryVector(vector)
      : vector;

    Object.entries(categoryVector).forEach(([group, groupVector]) => {
      if (group in jsonData) {
        const { categories } = this.flattenHierarchy(
          group,
          jsonData[group as keyof typeof jsonData]
        );

        console.log(`\nGroup: ${group}`);
        groupVector.forEach((value: number, index: number) => {
          if (value > 0) {
            const category = categories[index];
            if (category) {
              const path = category.path;
              console.log(`Position ${index}: ${path[path.length - 1]}`);
            }
          }
        });
      }
    });
  }
}
